import React, { useState, useEffect } from 'react';
import {
  getSupportCategories,
  createSupportCategory,
  updateSupportCategory,
  deleteSupportCategory
} from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Alert from '../../components/common/Alert';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import DataTable from '../../components/common/DataTable';
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiEye,
  FiEyeOff,
  FiGrid,
  FiLayers,
  FiPackage,
  FiFilter,
  FiRefreshCw,
  FiSearch,
  FiChevronDown,
  FiChevronUp,
  FiAlertCircle,
  FiCheckCircle,
  FiX,
  FiStar,
  FiTrendingUp,
  FiTrendingDown,
  FiBarChart2,
  FiSettings,
  FiHash,
  FiTag,
  FiMessageSquare,
  FiClock,
  FiActivity,
  FiShield
} from 'react-icons/fi';

const SupportCategories = ({ admin }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: '📁',
    priority: 'medium',
    is_active: true
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, active, inactive
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await getSupportCategories();
      
      if (response.success && response.data) {
        setCategories(response.data || []);
      } else {
        setCategories(response.data || response || []);
      }
    } catch (err) {
      Alert.error('Failed to fetch categories: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      Alert.error('Category name is required');
      return;
    }

    try {
      if (editingCategory) {
        const updateData = {
          category_id: editingCategory.category_id,
          ...formData
        };
        await updateSupportCategory(updateData);
        Alert.success('Category updated successfully');
      } else {
        await createSupportCategory(formData);
        Alert.success('Category created successfully');
      }
      
      setShowModal(false);
      setEditingCategory(null);
      setFormData({
        name: '',
        description: '',
        icon: '📁',
        priority: 'medium',
        is_active: true
      });
      fetchCategories();
    } catch (err) {
      Alert.error(err.message || 'Failed to save category');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name || '',
      description: category.description || '',
      icon: category.icon || '📁',
      priority: category.priority || 'medium',
      is_active: category.is_active !== undefined ? category.is_active : true
    });
    setShowModal(true);
  };

  const handleDelete = async (categoryId) => {
    const category = categories.find(c => c.category_id === categoryId);
    if (!category) return;

    if (window.confirm(`Are you sure you want to delete "${category.name}"? This action cannot be undone.`)) {
      try {
        await deleteSupportCategory({ category_id: categoryId });
        Alert.success('Category deleted successfully');
        fetchCategories();
      } catch (err) {
        Alert.error('Failed to delete category: ' + err.message);
      }
    }
  };

  const handleToggleStatus = async (categoryId, currentStatus) => {
    try {
      const category = categories.find(c => c.category_id === categoryId);
      if (!category) return;

      const updateData = {
        category_id: categoryId,
        is_active: !currentStatus
      };

      await updateSupportCategory(updateData);
      Alert.success(`Category ${!currentStatus ? 'activated' : 'deactivated'}`);
      fetchCategories();
    } catch (err) {
      Alert.error('Failed to update category status: ' + err.message);
    }
  };

  const iconOptions = [
    '📁', '📋', '🔧', '💰', '🛒', '🚚', '💳', '🔄',
    '❓', '⚠️', '✅', '❌', '📱', '💻', '🖨️', '📷',
    '🔒', '🔓', '📊', '📈', '📉', '🔔', '📢', '📣',
    '🏷️', '📝', '🔍', '📌', '📍', '🎯', '⚡', '🌟'
  ];

  // Filter categories
  const filteredCategories = categories.filter(category => {
    // Search filter
    const matchesSearch = !searchTerm || 
      category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (category.description && category.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    // Status filter
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && category.is_active) ||
      (statusFilter === 'inactive' && !category.is_active);
    
    return matchesSearch && matchesStatus;
  });

  // Calculate stats
  const activeCount = categories.filter(c => c.is_active).length;
  const inactiveCount = categories.length - activeCount;
  const totalTickets = categories.reduce((sum, cat) => sum + (cat.ticket_count || 0), 0);
  const highPriorityCount = categories.filter(c => c.priority === 'high').length;

  const getPriorityConfig = (priority) => {
    const configs = {
      'high': { color: 'text-red-400', bg: 'bg-red-900/30', border: 'border-red-800/50', icon: <FiAlertCircle /> },
      'medium': { color: 'text-yellow-400', bg: 'bg-yellow-900/30', border: 'border-yellow-800/50', icon: <FiBarChart2 /> },
      'low': { color: 'text-blue-400', bg: 'bg-blue-900/30', border: 'border-blue-800/50', icon: <FiTrendingDown /> }
    };
    return configs[priority] || configs.medium;
  };

  // Apply dark theme styling
  const customStyles = {
    container: "min-h-screen bg-gray-900 p-4 md:p-6",
    headerTitle: "text-2xl md:text-3xl font-bold text-white",
    headerSubtitle: "text-gray-400 mt-1",
    card: "bg-gray-800 rounded-xl border border-gray-700 p-6",
    buttonPrimary: "bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-all duration-200",
    buttonSecondary: "bg-gray-700 text-white border border-gray-600 rounded-lg hover:bg-gray-600 transition-colors",
    input: "w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white placeholder-gray-500",
    select: "w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent text-white",
    badge: "inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
  };

  if (loading && categories.length === 0) {
    return (
      <div className={customStyles.container}>
        <LoadingSpinner />
      </div>
    );
  }

  const columns = [
    {
      header: 'Category',
      accessor: 'name',
      cell: (row) => (
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 rounded-lg bg-gray-700 flex items-center justify-center">
            <span className="text-2xl">{row.icon || '📁'}</span>
          </div>
          <div>
            <p className="font-medium text-white">{row.name}</p>
            {row.description && (
              <p className="text-sm text-gray-400 mt-1">{row.description}</p>
            )}
          </div>
        </div>
      )
    },
    {
      header: 'Priority',
      accessor: 'priority',
      cell: (row) => {
        const config = getPriorityConfig(row.priority);
        return (
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${config.bg} ${config.color} ${config.border}`}>
            {config.icon}
            <span className="ml-1">{row.priority || 'medium'}</span>
          </span>
        );
      }
    },
    {
      header: 'Tickets',
      accessor: 'ticket_count',
      cell: (row) => (
        <div className="text-center">
          <span className="font-bold text-white text-lg">{row.ticket_count || 0}</span>
          <div className="text-xs text-gray-400">tickets</div>
        </div>
      )
    },
    {
      header: 'Status',
      accessor: 'is_active',
      cell: (row) => (
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
          row.is_active 
            ? 'bg-green-900/30 text-green-400 border border-green-800/50' 
            : 'bg-gray-700 text-gray-400 border border-gray-600'
        }`}>
          {row.is_active ? <FiEye className="mr-1" /> : <FiEyeOff className="mr-1" />}
          {row.is_active ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      header: 'Created',
      accessor: 'created_at',
      cell: (row) => (
        <div className="text-sm text-gray-400">
          {row.created_at ? new Date(row.created_at).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
          }) : '-'}
        </div>
      )
    },
    {
      header: 'Actions',
      accessor: 'actions',
      cell: (row) => (
        <div className="flex space-x-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleEdit(row)}
            className="bg-gray-700 hover:bg-gray-600 text-white border-gray-600"
          >
            <FiEdit2 className="mr-1" />
            Edit
          </Button>
          <Button
            size="sm"
            variant={row.is_active ? "danger" : "success"}
            onClick={() => handleToggleStatus(row.category_id, row.is_active)}
            className={row.is_active 
              ? "bg-red-900/30 hover:bg-red-900/50 text-red-400 hover:text-red-300 border border-red-800/50"
              : "bg-green-900/30 hover:bg-green-900/50 text-green-400 hover:text-green-300 border border-green-800/50"
            }
          >
            {row.is_active ? <FiEyeOff className="mr-1" /> : <FiEye className="mr-1" />}
            {row.is_active ? 'Deactivate' : 'Activate'}
          </Button>
          <Button
            size="sm"
            variant="danger"
            onClick={() => handleDelete(row.category_id)}
            disabled={(row.ticket_count || 0) > 0}
            title={row.ticket_count > 0 ? 'Cannot delete category with tickets' : ''}
            className="bg-red-900/30 hover:bg-red-900/50 text-red-400 hover:text-red-300 border border-red-800/50 disabled:opacity-50"
          >
            <FiTrash2 className="mr-1" />
            Delete
          </Button>
        </div>
      )
    }
  ];

  return (
    <div className={customStyles.container}>
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className={customStyles.headerTitle}>
              <span className="text-[#ff660e]">Support</span> Categories
            </h1>
            <p className={customStyles.headerSubtitle}>
              Manage ticket categories, priorities, and organization
            </p>
          </div>
          <Button
            variant="primary"
            onClick={() => {
              setEditingCategory(null);
              setFormData({
                name: '',
                description: '',
                icon: '📁',
                priority: 'medium',
                is_active: true
              });
              setShowModal(true);
            }}
            className={customStyles.buttonPrimary}
          >
            <FiPlus className="mr-2" />
            New Category
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-br from-blue-900/30 to-blue-800/20 rounded-xl border border-blue-700/30 p-5 hover:border-blue-600/50 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Total Categories</p>
              <p className="text-2xl font-bold text-white mt-1 group-hover:text-blue-300 transition-colors">
                {categories.length}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {activeCount} active, {inactiveCount} inactive
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-blue-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
              <FiGrid className="text-blue-400 text-2xl" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-900/30 to-green-800/20 rounded-xl border border-green-700/30 p-5 hover:border-green-600/50 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Active Categories</p>
              <p className="text-2xl font-bold text-white mt-1 group-hover:text-green-300 transition-colors">
                {activeCount}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {Math.round((activeCount / categories.length) * 100) || 0}% active rate
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-green-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
              <FiActivity className="text-green-400 text-2xl" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-900/30 to-purple-800/20 rounded-xl border border-purple-700/30 p-5 hover:border-purple-600/50 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Total Tickets</p>
              <p className="text-2xl font-bold text-white mt-1 group-hover:text-purple-300 transition-colors">
                {totalTickets}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                Across all categories
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-purple-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
              <FiPackage className="text-purple-400 text-2xl" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-amber-900/30 to-amber-800/20 rounded-xl border border-amber-700/30 p-5 hover:border-amber-600/50 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">High Priority</p>
              <p className="text-2xl font-bold text-white mt-1 group-hover:text-amber-300 transition-colors">
                {highPriorityCount}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                Categories with high priority
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-amber-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
              <FiAlertCircle className="text-amber-400 text-2xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters Card */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 mb-6">
        <div className="p-4 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <FiFilter className="text-[#ff660e]" />
              <h3 className="font-medium text-white">Filters & Search</h3>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="text-sm text-[#ff660e] hover:text-orange-400 font-medium flex items-center transition-colors"
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="p-4 md:p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Search */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Search Categories</label>
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                  <input
                    type="text"
                    placeholder="Search by name or description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white placeholder-gray-500"
                  />
                </div>
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active Only</option>
                  <option value="inactive">Inactive Only</option>
                </select>
              </div>

              {/* Priority Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Priority</label>
                <select
                  className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white"
                  onChange={(e) => {
                    // Add priority filter functionality
                  }}
                >
                  <option value="">All Priorities</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
            </div>

            {/* Filter Actions */}
            <div className="flex justify-between items-center mt-6 pt-6 border-t border-gray-700">
              <button
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                }}
                className="px-4 py-2 text-gray-400 hover:text-gray-300 text-sm font-medium hover:bg-gray-700 rounded-lg transition-colors"
              >
                Clear All Filters
              </button>
              <button
                onClick={() => setShowFilters(false)}
                className="px-4 py-2 text-gray-300 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 transition-colors font-medium hover:scale-[1.02]"
              >
                Apply Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Categories Table */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        <DataTable
          columns={columns}
          data={filteredCategories}
          searchable={false} // Using custom search
          emptyMessage={
            <div className="text-center py-12">
              <div className="text-gray-600 text-6xl mb-6">📁</div>
              <h3 className="text-xl font-medium text-white mb-2">No categories found</h3>
              <p className="text-gray-400 mb-6">
                {categories.length === 0 
                  ? 'Create your first support category to get started'
                  : 'Try adjusting your search filters'}
              </p>
              {categories.length === 0 && (
                <Button
                  variant="primary"
                  onClick={() => setShowModal(true)}
                  className={customStyles.buttonPrimary}
                >
                  <FiPlus className="mr-2" />
                  Create First Category
                </Button>
              )}
            </div>
          }
        />
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingCategory(null);
          setFormData({
            name: '',
            description: '',
            icon: '📁',
            priority: 'medium',
            is_active: true
          });
        }}
        title={editingCategory ? 'Edit Category' : 'Create New Category'}
        size="md"
        className="bg-gray-800 border border-gray-700"
      >
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Category Name *
              </label>
              <input
                type="text"
                className={customStyles.input}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Technical Support, Billing Issue"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Description
              </label>
              <textarea
                className={`${customStyles.input} h-24`}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this category"
              />
            </div>

            {/* Icon */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Icon
              </label>
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 border border-gray-600 rounded-lg flex items-center justify-center text-2xl bg-gray-700">
                  {formData.icon}
                </div>
                <select
                  className={customStyles.select}
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                >
                  {iconOptions.map((icon, index) => (
                    <option key={index} value={icon}>
                      {icon} Select Icon
                    </option>
                  ))}
                </select>
              </div>
              <p className="text-xs text-gray-400 mt-1">Select an icon to represent this category</p>
            </div>

            {/* Priority */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Default Priority
              </label>
              <select
                className={customStyles.select}
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
              <p className="text-xs text-gray-400 mt-1">
                Default priority for tickets in this category
              </p>
            </div>

            {/* Status */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                className="h-4 w-4 text-[#ff660e] focus:ring-[#ff660e] border-gray-600 rounded bg-gray-700"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
              <label htmlFor="is_active" className="ml-2 block text-sm text-gray-300">
                Active (visible for ticket creation)
              </label>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-2 pt-4 border-t border-gray-700">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowModal(false);
                  setEditingCategory(null);
                  setFormData({
                    name: '',
                    description: '',
                    icon: '📁',
                    priority: 'medium',
                    is_active: true
                  });
                }}
                className="bg-gray-700 hover:bg-gray-600 text-white border-gray-600"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                variant="primary"
                className={customStyles.buttonPrimary}
              >
                {editingCategory ? 'Update' : 'Create'} Category
              </Button>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default SupportCategories;